import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { ConnectionStatus, TranscriptEntry, TranscriptSpeaker } from "@/modules/speech-to-text/types"

export interface ITranscriptState {
  agentTranscripts: TranscriptEntry[]
  patientTranscripts: TranscriptEntry[]
  agentIsRecording: boolean
  patientIsRecording: boolean
  agentConnectionStatus: ConnectionStatus
  patientConnectionStatus: ConnectionStatus
}

export interface ITranscriptActions {
  addTranscript: (speaker: TranscriptSpeaker, entry: Omit<TranscriptEntry, "id">) => void
  clearTranscripts: (speaker?: TranscriptSpeaker) => void
  setIsRecording: (speaker: TranscriptSpeaker, isRecording: boolean) => void
  setConnectionStatus: (speaker: TranscriptSpeaker, status: ConnectionStatus) => void
  getMergedTranscripts: () => TranscriptEntry[]
}

const initialState: ITranscriptState = {
  agentTranscripts: [],
  patientTranscripts: [],
  agentIsRecording: false,
  patientIsRecording: false,
  agentConnectionStatus: "disconnected",
  patientConnectionStatus: "disconnected",
}

const useTranscriptStore = create<ITranscriptState & ITranscriptActions>()(
  devtools(
    (set, get) => ({
      ...initialState,
      addTranscript: (speaker, entry) => {
        const newEntry = {
          ...entry,
          id: `${speaker}-${entry.timestamp}-${Math.random().toString(36).slice(2, 9)}`,
        }
        console.log(`[TranscriptStore] Adding ${speaker} transcript:`, newEntry.text)

        if (speaker === "AGENT") {
          set((state) => ({
            agentTranscripts: [...state.agentTranscripts, newEntry],
          }))
        } else {
          set((state) => ({
            patientTranscripts: [...state.patientTranscripts, newEntry],
          }))
        }
      },
      clearTranscripts: (speaker) => {
        console.log(`[TranscriptStore] Clearing transcripts for: ${speaker ?? "all"}`)
        if (speaker === "AGENT") {
          set({ agentTranscripts: [] })
        } else if (speaker === "PATIENT") {
          set({ patientTranscripts: [] })
        } else {
          set({ agentTranscripts: [], patientTranscripts: [] })
        }
      },
      setIsRecording: (speaker, isRecording) => {
        console.log(`[TranscriptStore] setIsRecording for ${speaker}: ${isRecording}`)
        if (speaker === "AGENT") {
          set({ agentIsRecording: isRecording })
        } else {
          set({ patientIsRecording: isRecording })
        }
      },
      setConnectionStatus: (speaker, connectionStatus) => {
        console.log(`[TranscriptStore] setConnectionStatus for ${speaker}: ${connectionStatus}`)
        if (speaker === "AGENT") {
          set({ agentConnectionStatus: connectionStatus })
        } else {
          set({ patientConnectionStatus: connectionStatus })
        }
      },
      getMergedTranscripts: () => {
        const { agentTranscripts, patientTranscripts } = get()
        const merged = [...agentTranscripts, ...patientTranscripts]
        return merged.sort((a, b) => a.timestamp - b.timestamp)
      },
    }),
    { name: "transcript" },
  ),
)

export default useTranscriptStore
